import React, { useState } from "react";
import { Button } from "@shared/ui/button";
import { Download, RotateCcw, Check, BarChart3, Loader2 } from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  LabelList,
} from "recharts";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Tabs } from "@shared/ui/tabs";
import { ResolveDialog } from "./ResolveDialog";
import { QuickStatsCard } from "@shared/components/QuickStatsCard";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { useParams } from "react-router-dom";
import {
  useBulkPaymentBatchRecords,
  usePostMatchedBulkPaymentBatch,
  useExportExceptionsBulkPaymentBatch,
} from "@features/cx/paymentsv2/hooks";
import { useToast } from "@shared/hooks/use-toast";
import { PAGE_SIZE } from "@shared/utils/constants";

interface RecordsTabProps {
  data?: any;
}

export const RecordsTab = ({ data }: RecordsTabProps) => {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const { mutate: postMatched, isPending: isPosting } =
    usePostMatchedBulkPaymentBatch(Number(id));
  const { mutate: exportExceptions, isPending: isExporting } =
    useExportExceptionsBulkPaymentBatch(Number(id));

  const handlePostMatched = () => {
    postMatched(undefined, {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Successfully posted matched payments.",
        });
      },
    });
  };

  const handleExportExceptions = () => {
    exportExceptions(undefined, {
      onSuccess: (blobData: any) => {
        const blob = new Blob([blobData], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `exceptions_${data?.batchId || id}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);

        toast({
          title: "Success",
          description: "Exceptions exported successfully.",
        });
      },
      onError: () => {
        toast({
          title: "Error",
          description: "Failed to export exceptions.",
          variant: "destructive",
        });
      },
    });
  };
  const currency = useCurrency();

  const paymentModeData = React.useMemo(() => {
    if (!data?.paymentModeBreakdown) return [];
    const maxAmount = Math.max(
      ...data.paymentModeBreakdown.map((item: any) => item.totalAmount || 0),
    );
    return data.paymentModeBreakdown.map((item: any) => ({
      mode: item.mode,
      amount: `${currency} ${item.totalAmount || 0}`,
      count: item.count,
      percent: maxAmount > 0 ? ((item.totalAmount || 0) / maxAmount) * 100 : 0,
    }));
  }, [data?.paymentModeBreakdown]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "Posted":
        return (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-md border border-green-200 bg-green-50 text-green-700">
            Posted
          </span>
        );
      case "Duplicate":
        return (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700">
            Duplicate
          </span>
        );
      case "Matched":
        return (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-md border border-green-200 bg-green-50 text-green-700">
            Matched
          </span>
        );
      case "Unmatched":
        return (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-md border border-amber-200 bg-amber-50 text-amber-700">
            Unmatched
          </span>
        );
      case "Failed":
        return (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-md border border-red-200 bg-red-50 text-red-700">
            Failed
          </span>
        );
      default:
        return <span>{status}</span>;
    }
  };

  const STATS_DATA = [
    {
      title: "Total Records",
      displayValue: <span className="text-gray-900">{data?.totalRecords}</span>,
      trendSummary: "8 KB file",
    },
    {
      title: "Matched",
      displayValue: (
        <span className="text-green-600">{data?.matchedCount}</span>
      ),
      trendSummary: "Auto-matched to a consumer",
    },
    {
      title: "Unmatched",
      displayValue: (
        <span className="text-amber-600">{data?.unmatchedCount}</span>
      ),
      trendSummary: "Needs manual review",
    },
    {
      title: "Duplicates",
      displayValue: (
        <span className="text-orange-600">{data?.duplicateCount}</span>
      ),
      trendSummary: "Same txn seen twice",
    },
    {
      title: "Failed",
      displayValue: <span className="text-red-600">{data?.failedCount}</span>,
      trendSummary: "Validation errors",
    },
  ];

  const columns: TableColumn<any>[] = [
    { key: "txnRef", header: "Txn Ref" },
    {
      key: "consumer",
      header: "Consumer",
      render: (val: any, row: any) => (
        <div className="flex flex-col">
          <span
            className={`font-semibold ${row.consumerName === "Unknown / Missing" ? "text-gray-400 italic font-normal" : "text-gray-900"}`}
          >
            {row.consumerName}
          </span>
          {row.consumerAcct && (
            <span className="text-xs text-gray-500">{row.consumerAcct}</span>
          )}
        </div>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      render: (val: any, row: any) => (
        <span className="font-semibold text-gray-900">{row.amount}</span>
      ),
    },
    { key: "mode", header: "Mode" },
    { key: "source", header: "Source" },
    { key: "valueDate", header: "Payment Date" },
    {
      key: "status",
      header: "Status",
      render: (val: any, row: any) => getStatusBadge(row.status),
    },
    {
      key: "match",
      header: "Match",
      render: (val: any, row: any) => (
        <div className="flex items-center gap-2">
          {row.status === "Matched" && (
            <div className="h-0.5 w-4 bg-green-600 rounded"></div>
          )}
          {row.status === "Unmatched" && (
            <div className="h-0.5 w-4 bg-red-500 rounded"></div>
          )}
          {row.status !== "Matched" && row.status !== "Unmatched" && (
            <span className="text-gray-400">—</span>
          )}
          <span className={`text-sm font-semibold ${row.matchClass}`}>
            {row.matchStr !== "—" ? row.matchStr : ""}
          </span>
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (val: any, row: any) => {
        if (row.status !== "Unmatched") {
          return <span className="text-gray-400">—</span>;
        }
        return (
          <Button
            size="sm"
            onClick={() => setSelectedRow(row)}
            variant="default"
          >
            Resolve
          </Button>
        );
      },
    },
  ];

  const TableContent = ({ statusTab }: { statusTab: string }) => {
    const { id } = useParams<{ id: string }>();
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");

    const { data: recordsResponse, isLoading } = useBulkPaymentBatchRecords({
      id: Number(id),
      status: statusTab,
      search,
      page,
      pageSize: PAGE_SIZE,
    });

    const mappedRecords = React.useMemo(() => {
      if (!recordsResponse?.results) return [];
      return recordsResponse.results.map((r: any) => ({
        id: r.id,
        txnRef: r.txnRef || "N/A",
        consumerName: r.consumerName || "N/A",
        consumerAcct: r.consumerAccount || "N/A",
        amount: `${currency} ${r.amount}`,
        mode: r.paymentMode,
        source: data?.sourceSummary?.paymentSource || "N/A",
        valueDate: r.valueDate || "N/A",
        status: r.statusDisplay || "N/A",
        matchStr: r.matchConfidence
          ? `${parseFloat(r.matchConfidence)}% Match`
          : "No Match",
        matchClass:
          parseFloat(r.matchConfidence) >= 90
            ? "text-green-600"
            : parseFloat(r.matchConfidence) > 0
              ? "text-amber-600"
              : "text-red-500",
        showResolve: r.showResolve,
        showPost: r.showPost,
      }));
    }, [
      recordsResponse?.results,
      currency,
      data?.sourceSummary?.paymentSource,
    ]);

    return (
      <div className="mt-4">
        <GenericTable
          data={mappedRecords}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          onSearch={(val) => {
            setSearch(val);
            setPage(1);
          }}
          searchPlaceholder="Search txn ref, consumer, amount..."
          pagination={{
            currentPage: page,
            totalPages: Math.ceil((recordsResponse?.count || 0) / 10),
            totalItems: recordsResponse?.count || 0,
            itemsPerPage: 10,
            startItem: (page - 1) * 10 + 1,
            endItem: Math.min(page * 10, recordsResponse?.count || 0),
          }}
          onPageChange={(p) => setPage(p)}
        />
      </div>
    );
  };

  const tabComponents = {
    all: {
      label: "All",
      count: data?.totalRecords || 0,
      component: <TableContent statusTab="" />,
    },
    matched: {
      label: "Matched",
      count: data?.matchedCount || 0,
      component: <TableContent statusTab="matched" />,
    },
    unmatched: {
      label: "Unmatched",
      count: data?.unmatchedCount || 0,
      component: <TableContent statusTab="unmatched" />,
    },
    duplicates: {
      label: "Duplicates",
      count: data?.duplicateCount || 0,
      component: <TableContent statusTab="duplicate" />,
    },
    failed: {
      label: "Failed",
      count: data?.failedCount || 0,
      component: <TableContent statusTab="failed" />,
    },
  };

  const urlMapping = {
    all: "all",
    matched: "matched",
    unmatched: "unmatched",
    duplicates: "duplicates",
    failed: "failed",
  };

  return (
    <div className="space-y-6 pt-4">
      {/* Summary Stats Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {STATS_DATA.map((stat, index) => (
          <QuickStatsCard
            key={index}
            title={stat.title}
            count={stat.displayValue}
            trendSummary={stat.trendSummary}
          />
        ))}
      </div>
      {/* Batch Breakdown Section */}
      <div className="bg-white border rounded-xl p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <BarChart3 className="w-4 h-4 text-gray-500" />
          <h2 className="text-base font-bold text-gray-900">Batch Breakdown</h2>
        </div>
        <p className="text-xs text-gray-500 mb-6">
          Totals by source, payment mode, and (where applicable) kiosk location.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-1 gap-12">
          {/* By Payment Mode */}
          <div>
            <div className="space-y-3">
              {paymentModeData.map((item, i) => (
                <div key={i} className="flex items-center gap-6 text-sm">
                  <div 
                    className="w-[200px] shrink-0 text-gray-700 font-medium truncate"
                    title={item.mode}
                  >
                    {item.mode}
                  </div>
                  <div className="flex-grow h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-600 rounded-full"
                      style={{ width: `${item.percent}%` }}
                    ></div>
                  </div>
                  <div className="w-[120px] shrink-0 text-right font-bold text-gray-900">
                    {item.amount}
                  </div>
                  <div className="w-[40px] shrink-0 text-right text-gray-400">
                    {item.count}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Records Section */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-gray-900">Records</h2>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="text-gray-600"
              onClick={handleExportExceptions}
              disabled={isExporting}
            >
              {isExporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export Exceptions
            </Button>
            <Button
              className="bg-blue-400 hover:bg-blue-500 text-white border-0"
              onClick={handlePostMatched}
              disabled={
                isPosting || (data?.status !== undefined && data.status >= 2)
              }
            >
              {isPosting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Post Matched
            </Button>
          </div>
        </div>

        <Tabs
          defaultValue="all"
          className="w-full"
          urlMapping={urlMapping}
          tabComponents={tabComponents}
          level={1}
        />
      </div>

      {selectedRow && (
        <ResolveDialog
          isOpen={!!selectedRow}
          onClose={() => setSelectedRow(null)}
          rowData={selectedRow}
        />
      )}
    </div>
  );
};
